import { Component, EventEmitter, OnDestroy, OnInit, Input, Output } from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatButton } from "@angular/material/button";
import { Subscription } from 'rxjs';
import { clearServerValidationError } from '../registration-validation';

interface Status {
  loading: boolean;
}

@Component({
    selector: 'app-tenant-info',
    templateUrl: './tenant-info.component.html',
    styleUrls: ['./tenant-info.component.css'],
    standalone: false
})
export class TenantInfoComponent implements OnInit, OnDestroy {
  @Input() startingForm!: FormGroup;
  @Input() finalForm: boolean = false;
  @Input() status: Status = { loading: false };

  @Output() subformInitialized: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Output() changeStep: EventEmitter<string> = new EventEmitter<string>();
  @Output() submitForm: EventEmitter<any> = new EventEmitter<MatButton>();

  public tenantInfoForm!: FormGroup;
  private readonly controlSubscriptions: Subscription[] = [];
  private lastGeneratedTenantName = '';
  private tenantNameOverridden = false;
  private tenantRandomSuffix = '';

  constructor(private _fb: FormBuilder) { }

  ngOnInit() {
    if (this.startingForm) {
      this.tenantInfoForm = this._fb.group(this.startingForm);
    } else {
      this.tenantInfoForm = this._fb.group({
        companyName: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
        tenantName: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern(/^[a-z0-9\-]*$/)]),
        username: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9.]*$/)]),
        firstName: new FormControl('', [Validators.required, Validators.maxLength(20)]),
        lastName: new FormControl('', [Validators.required, Validators.maxLength(20)]),
        emailAddress: new FormControl('', [Validators.required, Validators.email]),
      })
    }
    this.resetServerValidationOnInput();
    this.initializeTenantNameAutofill();
    this.subformInitialized.emit(this.tenantInfoForm);
  }

  ngOnDestroy() {
    this.controlSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  errorMessage(item: AbstractControl | null): string {
    if (!item) {
      return '';
    }
    if (item.hasError('required')) {
      return 'This field is required';
    } else if (item.hasError('email')) {
      return 'Please enter a valid email address';
    } else if (item.hasError('minlength')) {
      return 'This field must be at least ' + item.errors?.['minlength'].requiredLength + ' characters';
    } else if (item.hasError('maxlength')) {
      return 'This field must be at most ' + item.errors?.['maxlength'].requiredLength + ' characters';
    } else if (item.hasError('pattern')) {
      return item === this.tenantInfoForm.get('username')
        ? 'This field must contain only letters, numbers, and .'
        : 'This field must contain only a-z 0-9 and -';
    } else if (item.hasError('server')) {
      return item.getError('server');
    }
    return '';
  };


  doChangeStep(direction: 'forward') {
    this.changeStep.emit(direction);
  }

  doSubmitForm() {
    this.submitForm.emit();
  }

  private resetServerValidationOnInput(): void {
    Object.values(this.tenantInfoForm.controls).forEach((control) => {
      this.controlSubscriptions.push(
        control.valueChanges.subscribe(() => clearServerValidationError(control))
      );
    });
  }

  private initializeTenantNameAutofill() {
    const companyNameField = this.tenantInfoForm.get('companyName');
    const tenantNameField = this.tenantInfoForm.get('tenantName');

    if (!companyNameField || !tenantNameField) {
      return;
    }

    this.tenantNameOverridden = Boolean(tenantNameField.value);

    companyNameField.valueChanges.subscribe((companyName: string) => {
      if (!this.shouldAutofillTenantName(tenantNameField)) {
        return;
      }

      this.autofillTenantName(tenantNameField, companyName);
    });

    tenantNameField.valueChanges.subscribe((tenantName: string) => {
      this.tenantNameOverridden = Boolean(tenantName) && tenantName !== this.lastGeneratedTenantName;
    });

    if (!tenantNameField.value && companyNameField.value) {
      this.autofillTenantName(tenantNameField, companyNameField.value);
    }
  }

  private shouldAutofillTenantName(tenantNameField: AbstractControl): boolean {
    return !tenantNameField.value || tenantNameField.value === this.lastGeneratedTenantName || !this.tenantNameOverridden;
  }

  private autofillTenantName(tenantNameField: AbstractControl, companyName: string) {
    const generatedTenantName = this.generateTenantName(companyName);

    this.lastGeneratedTenantName = generatedTenantName;
    tenantNameField.setValue(generatedTenantName, { emitEvent: false });
  }

  private generateTenantName(companyName: string): string {
    const firstWord = companyName
      ?.trim()
      .split(/\s+/)[0]
      ?.toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/^-+|-+$/g, '');
    const tenantPrefix = (firstWord || '').slice(0, 45);

    if (!tenantPrefix) {
      return '';
    }

    if (!this.tenantRandomSuffix) {
      this.tenantRandomSuffix = this.randomDigits(5);
    }

    //return `${tenantPrefix}${this.tenantRandomSuffix}`;
    return tenantPrefix;
  }

  private randomDigits(length: number): string {
    return Array.from({ length }, () => Math.floor(Math.random() * 10)).join('');
  }
}
